import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Facebook from "../../assets/logo/facebook.svg";
// import Instagram from "../../assets/logo/instagram-icon.svg";
// import DownloadReportIcon from "../../assets/dasboard/download-report-image.svg";
import linkIcon from "../../assets/dasboard/link-icon.svg";
import copyIcon from "../../assets/dasboard/copy-icon.svg";
// import copyIconHover from "../../assets/dasboard/copy-icon-hover.svg";
import {
  AssessmentForSamePersonalityNameAndRole,
  AssessmentForLongText,
  getColorCode,
  REPORT_ROUTES,
  convertToLowerCase,
  addSpacesOnCaps,
} from "../../utils/utility-functions";
import { FacebookShareButton, TwitterShareButton } from "react-share";
import { ROUTE_CONSTANT } from "../../routing/route-constants";
import PathHelper from "../../utils/path-helper";
import { useParams } from "react-router-dom";
import { LOCALE } from "../../utils/constants";
import { useTranslation } from "react-i18next";
import { ENNEAGRAM_TEST_ASSESSMENT_ID } from "../../utils/general-config";

const ShareProfileModal = ({
  isOpen,
  onClose,
  personalityImage,
  assessmentTitle,
  assessmentAttemptId,
  personalityType,
  personalityName,
  personalityGroup,
  userGUID,
  assessmentId,
  isRomanticCompatibility,
  assessmentAttemptResultId,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const { t } = useTranslation();
  const isEnneagramTest =
    convertToLowerCase(assessmentId) ===
    convertToLowerCase(ENNEAGRAM_TEST_ASSESSMENT_ID);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Resolve report route token from assessmentId (simple, similar to earlier)
  const assessmentIDValue = assessmentId?.toLowerCase?.();
  const entry = REPORT_ROUTES[assessmentIDValue];
  const reportRoute =
    typeof entry === "object"
      ? isRomanticCompatibility
        ? entry?.compatibility
        : entry?.personal
      : entry || ROUTE_CONSTANT.DEFAULT_REPORT;

  const path = PathHelper(reportRoute, currentLocale);

  const baseUrl = window.location.origin;
  const shareableLink = isRomanticCompatibility
    ? `${baseUrl}${path.next}?assessresid=${assessmentAttemptResultId}`
    : `${baseUrl}${path.next}?aai=${assessmentAttemptId}&uid=${userGUID}`;

  const TestshareableLink = shareableLink;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      toast.success(t("shareModal.link_copied"), {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error(t("shareModal.failed_copy_link"));
    }
  };

  if (!isOpen) return null;

  const personalityColorDark = getColorCode(personalityGroup)?.dark;
  const personalityColorLight = getColorCode(personalityGroup)?.light;
  const personalityColorMedium = getColorCode(personalityGroup)?.medium;

  const isSamePersonalityAssessment =
    AssessmentForSamePersonalityNameAndRole.includes(assessmentId);

  const isLongTextAssessment = AssessmentForLongText.includes(assessmentId);
  const isSpanishLocale = currentLocale === LOCALE.ES;

  // Use short versions for Spanish locale and long text assessments
  const useShortText = isSpanishLocale && isLongTextAssessment;

  const shareTextForSamePersonalityNameAndRole = `${t(
    useShortText ? "shareModal.i_just_took_short" : "shareModal.i_just_took"
  )} ${assessmentTitle} ${t(
    useShortText ? "shareModal.found_out_short" : "shareModal.found_out"
  )} ${t("shareModal.my_result")} ${t(
    useShortText ? "shareModal.whats_your_short" : "shareModal.whats_your"
  )}`;

  const shareTextForDifferentPersonalityNameAndRole = `${t(
    useShortText ? "shareModal.i_just_took_short" : "shareModal.i_just_took"
  )} ${assessmentTitle} ${t(
    useShortText
      ? "shareModal.found_out_i_am_short"
      : "shareModal.found_out_i_am"
  )} ${
    isEnneagramTest
      ? addSpacesOnCaps(personalityName)
      : `${personalityName} (${personalityType})`
  }. ${t(
    useShortText ? "shareModal.whats_your_short" : "shareModal.whats_your"
  )}`;

  const shareText = isSamePersonalityAssessment
    ? shareTextForSamePersonalityNameAndRole
    : shareTextForDifferentPersonalityNameAndRole;

  return (
    <>
      <div
        className="fixed inset-0 top-10 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      <div
        className={`fixed transform bg-white shadow z-[1000] overflow-y-auto lg:overflow-hidden
       ${
         isMobile
           ? "bottom-0 left-0 right-0 top-1/5 flex-col items-center rounded-t-[20px]"
           : "left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-[35px]"
       }
       w-full lg:max-w-max justify-center flex d:max-w-4xl 
        md:h-max p-[10px]`}
      >
        <div className="flex flex-col p-4 sm:p-6 bottom-0 max-w-max">
          {/* Header with Close Button */}
          <div className="flex justify-end mb-2 md:mb-4 flex-shrink-0">
            <button
              onClick={onClose}
              style={{
                color: personalityColorDark,
                backgroundColor: personalityColorLight,
              }}
              className="py-1 px-2 flex items-center text-base rounded-full transition-colors duration-200"
              onMouseEnter={(e) => (
                (e.currentTarget.style.backgroundColor = personalityColorDark),
                (e.currentTarget.style.color = "white")
              )}
              onMouseLeave={(e) => (
                (e.currentTarget.style.backgroundColor = personalityColorLight),
                (e.currentTarget.style.color = personalityColorDark)
              )}
            >
              {t("shareModal.close")}{" "}
              <span className="text-2xl ml-1">&times;</span>
            </button>
          </div>

          {/* Main Content */}
          <div className="flex flex-col md:flex-row-reverse gap-[10px] justify-between overflow-y-auto items-center px-10">
            {/* Left Side Avatar */}
            <div className="flex justify-center flex-shrink-0 w-full md:w-1/3 h-auto max-h-[200px] md:max-h-none">
              <div className="aspect-square h-auto max-h-[200px] md:max-h-none">
                <img
                  src={personalityImage}
                  alt="Personality Avatar"
                  className="w-full max-w-[300px] h-auto rounded-full object-contain"
                />
              </div>
            </div>

            {/* Right Side Content */}
            <div className="flex flex-col">
              {/* Header Text */}
              <div className="max-w-[500px] w-full font-['Montserrat']">
                <h1 className="text-xl sm:text-2xl md:text-4xl font-bold text-center md:text-left">
                  <span className="text-[#444c5e]">
                    {t("shareModal.share_results_label")}{" "}
                  </span>
                  <span style={{ color: personalityColorMedium }}>
                    {" "}
                    {t("shareModal.friend_text")}
                  </span>
                </h1>
              </div>

              {/* Subheader */}
              <div className="max-w-[500px] w-full mt-2 md:mt-4 font-['Montserrat'] text-sm sm:text-base md:text-xl text-center md:text-left">
                <p>
                  <span className="text-[#444c5e] font-medium">
                    {t("shareModal.share_result_description")}{" "}
                  </span>
                  <span
                    style={{ color: personalityColorMedium }}
                    className="font-bold"
                  >
                    {""} {assessmentTitle}
                  </span>
                  <span className="text-[#444c5e] font-medium">.</span>
                </p>
              </div>

              {/* Social Buttons */}
              <div className="flex justify-center md:justify-start gap-4 mt-4 md:mt-6">
                {/* <button
                  onClick={handleFacebookShare}
                  className="aspect-square w-14 sm:w-16 md:w-24 bg-[#d9d9d9] rounded-full flex items-center justify-center hover:bg-gray-300"
                >
                  <img src={Facebook} alt="Facebook" className="w-1/2 h-1/2" />
                </button> */}
                <FacebookShareButton
                  url={TestshareableLink}
                  // quote={description}
                  hashtag={shareText}
                >
                  <button
                    // onClick={handleFacebookShare}
                    className="aspect-square w-14 sm:w-16 md:w-24 bg-[#d9d9d9] rounded-full flex items-center justify-center hover:bg-gray-300"
                  >
                    <img
                      src={Facebook}
                      alt="Facebook"
                      className="w-1/2 h-1/2"
                    />
                  </button>
                </FacebookShareButton>
                <TwitterShareButton
                  url={TestshareableLink}
                  title={shareText}
                  hashtags={["insightfultraits", "personalitytest"]}
                >
                  <button
                    // onClick={handleTwitterShare}
                    className="aspect-square w-14 sm:w-16 md:w-24 bg-[#d9d9d9] rounded-full flex items-center justify-center hover:bg-gray-300"
                  >
                    <span className="text-2xl sm:text-3xl md:text-5xl font-medium font-['Montserrat']">
                      𝕏
                    </span>
                  </button>
                </TwitterShareButton>
                {/* <button
                  onClick={handleTwitterShare}
                  className="aspect-square w-14 sm:w-16 md:w-24 bg-[#d9d9d9] rounded-full flex items-center justify-center hover:bg-gray-300"
                >
                  <span className="text-2xl sm:text-3xl md:text-5xl font-medium font-['Montserrat']">
                    𝕏
                  </span>
                </button> */}
                {/* <button
                  onClick={handleInstagramShare}
                  className="aspect-square w-14 sm:w-16 md:w-24 bg-[#d9d9d9] rounded-full flex items-center justify-center hover:bg-gray-300"
                >
                  <img
                    src={Instagram}
                    alt="Instagram"
                    className="w-1/2 h-1/2"
                  />
                </button> */}
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="mt-4 md:mt-8 flex-shrink-0 md:px-10">
            <div className="font-['Montserrat'] text-lg sm:text-xl md:text-2xl font-bold mb-2 text-center md:text-left">
              <span className="text-[#444c5e]">
                {" "}
                {t("shareModal.your_text")}{" "}
              </span>
              <span style={{ color: personalityColorMedium }}>
                {assessmentTitle}
              </span>
              <span className="text-[#444c5e]">
                {" "}
                {t("shareModal.results_text")}{" "}
              </span>
            </div>
            <p className="text-[#444c5e] text-sm sm:text-base md:text-xl font-medium font-['Montserrat'] mb-2 text-center md:text-left">
              {t("shareModal.share_result_note")}
            </p>

            {/* Link Box */}
            <div className="relative flex items-center mb-2">
              <img
                src={linkIcon}
                alt="Link"
                className="absolute left-4 w-5 sm:w-6 h-5 sm:h-6 z-10"
              />
              <input
                type="text"
                value={shareableLink}
                readOnly
                className="w-full h-12 md:h-16 bg-white rounded-lg border border-[#d9d9d9] 
                 pl-12 pr-12 text-sm sm:text-base md:text-lg font-normal font-sans 
                 truncate"
              />
              <button
                onClick={handleCopyLink}
                className="absolute right-4 p-1 sm:p-2 hover:bg-gray-100 rounded"
              >
                <img
                  src={copyIcon}
                  alt="Copy"
                  className="w-5 sm:w-6 h-5 sm:h-6"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareProfileModal;
